import React, { useEffect, useMemo, useState } from 'react'
import PBIVisuals from '../customVisuals/CustomVisuals';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import { useCustomVisuals } from '../../hooks/useCustomVisuals';
import { apiService } from '../../services/apiClient';
import { apiUrl } from '../../services/apiUrl';
import BarChartLoader from '../layout/BarChartLoader';


const skeletonStyle = {
  backgroundColor: '#f0f0f0', // Set the background color for the skeleton
  position: 'relative',
  overflow: 'hidden',
  marginTop:"10px",
  width:"100%",
  border: "2px solid #eee",

};

const NO_FILTERS = [];
const ALL_VALUES = "__ALL__";

const filterKey = (filter) => `${filter?.target?.table}.${filter?.target?.column}`;

/** Match the column's own type: a numeric column rejects "2029" as a string. */
function coerceValue(value, sample) {
  if (typeof sample === "number" || (sample === undefined && /^-?\d+(\.\d+)?$/.test(value))) {
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber)) return asNumber;
  }
  return value;
}

/** A visual's saved filters with the shared bar's choices layered on top. */
function applySelections(visual, controls, selections) {
  const applied = new Map((visual.filters || []).map((filter) => [filterKey(filter), filter]));

  controls.forEach((control) => {
    const selected = selections[control.key];
    if (selected === undefined) return;

    if (selected === ALL_VALUES) {
      applied.delete(control.key);
      return;
    }

    // Built fresh: a picked value means "is this value", so it must not inherit
    // a saved exclusion's operator.
    const sample = applied.get(control.key)?.values?.[0];
    applied.set(control.key, {
      $schema: "http://powerbi.com/product/schema#basic",
      target: { table: control.table, column: control.column },
      filterType: 1,
      operator: "In",
      values: [coerceValue(selected, sample)],
      requireSingleSelection: false,
    });
  });

  return [...applied.values()];
}

/**
 * Turn the filters saved with each persona visual into one dropdown per
 * table/column.
 *
 * The options are the values an admin declared for that filter in the Add
 * Visuals dialog, falling back to whatever was captured — Power BI's embed API
 * exposes a slicer's selection, never its full member list, so there is nothing
 * else to read them from.
 */
function buildFilterControls(visuals) {
  const byKey = new Map();

  visuals.forEach((visual) => {
    (visual.filters || []).forEach((filter) => {
      const table = filter?.target?.table;
      const column = filter?.target?.column;
      if (!table || !column || !Array.isArray(filter.values)) return;

      const key = filterKey(filter);
      if (!byKey.has(key)) {
        byKey.set(key, { key, table, column, values: new Set(), reportIds: new Set() });
      }
      const control = byKey.get(key);
      const declared = visual.filterOptions?.[key];
      (Array.isArray(declared) && declared.length ? declared : filter.values).forEach((value) =>
        control.values.add(value)
      );
      control.reportIds.add(String(visual.report_id));
    });
  });

  return [...byKey.values()].map((control) => ({
    ...control,
    values: [...control.values],
    reportIds: [...control.reportIds],
  }));
}


const LandingKPIReport = (props) => {

  const { fetchCustomVisuals } = useCustomVisuals();
  const [reportVisuals, setReportVisuals] = useState([])
  const [Loading,setLoading] = useState(true)

  useEffect(() => {
    setLoading(true);
    fetchCustomVisuals(props.groupId)
      .then((data) => {
        setReportVisuals(data);
        setLoading(false);
      })
      .catch(() => {
        setReportVisuals([]);
        setLoading(false);
      });
  }, [fetchCustomVisuals, props.groupId]);

  const filterControls = useMemo(() => buildFilterControls(reportVisuals), [reportVisuals]);

  // One bar beats a popup per tile, but only when every tile answers to the same
  // filters — across reports a shared choice would mean different things.
  const isSingleReport = useMemo(
    () => new Set(reportVisuals.map((visual) => String(visual.report_id))).size === 1,
    [reportVisuals]
  );
  const useGlobalFilters = isSingleReport && filterControls.length > 0;

  const [draftSelections, setDraftSelections] = useState({});
  const [appliedSelections, setAppliedSelections] = useState({});
  const [storedSelections, setStoredSelections] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiService
      .get(apiUrl.aipdKpiFilterSelections(props.groupId))
      .then((response) => {
        if (!cancelled) setStoredSelections(response.data?.selections || {});
      })
      .catch(() => {
        if (!cancelled) setStoredSelections({});
      });
    return () => {
      cancelled = true;
    };
  }, [props.groupId]);

  // Opens on what the viewer last applied, falling back to what the admin saved.
  // Held back until the saved choices arrive, so the tiles embed once.
  useEffect(() => {
    if (storedSelections === null) return;

    const initial = Object.fromEntries(
      filterControls.map((control) => {
        if (storedSelections[control.key] !== undefined) {
          return [control.key, String(storedSelections[control.key])];
        }
        const saved = reportVisuals
          .flatMap((visual) => visual.filters || [])
          .find((filter) => filterKey(filter) === control.key);
        return [control.key, saved?.values?.[0] !== undefined ? String(saved.values[0]) : ALL_VALUES];
      })
    );
    setDraftSelections(initial);
    setAppliedSelections(initial);
  }, [filterControls, reportVisuals, storedSelections]);

  const applyGlobalFilters = () => {
    setAppliedSelections(draftSelections);
    apiService
      .post(apiUrl.aipdKpiFilterSelections(props.groupId), { selections: draftSelections })
      .catch((err) => {
        console.warn("Could not save filter selections:", err?.message);
      });
  };

  const globalFiltersByVisual = useMemo(() => {
    if (!useGlobalFilters) return new Map();
    return new Map(
      reportVisuals.map((visual) => [
        visual.visualName,
        applySelections(visual, filterControls, appliedSelections),
      ])
    );
  }, [useGlobalFilters, reportVisuals, filterControls, appliedSelections]);

  // Grouped once, and per report, so each tile gets the same array instance on
  // every render — a fresh array would reset the filters the user just applied.
  const optionsByReport = useMemo(() => {
    const map = new Map();
    filterControls.forEach((control) => {
      control.reportIds.forEach((reportId) => {
        if (!map.has(reportId)) map.set(reportId, []);
        map.get(reportId).push(control);
      });
    });
    return map;
  }, [filterControls]);

  const defaultRL = props.defaultRL
  const rlTypeData = props.rlType




  return (
    <>
      {Loading && (
        <div style={{height:"420px"}} className="customVisuals_flex">
          <div style={skeletonStyle}>
            <div
              style={{
                position: "absolute",
                background: "white",
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="pageLoading"
            >
              <BarChartLoader />
            </div>
          </div>

          <div style={skeletonStyle}>
            <div
              style={{
                position: "absolute",
                background: "white",
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="pageLoading"
            >
              <BarChartLoader /> 
            </div>
          </div>

        </div>
      )}

      {!Loading && reportVisuals.length === 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            padding: "3rem 1rem",
            textAlign: "center",
            color: "#9ca3af",
            fontSize: "14px",
            border: "1px dashed #e5e7eb",
            borderRadius: "10px",
            marginTop: "10px",
            background: "#fafafa",
          }}
        >
          <InsertChartOutlinedIcon style={{ fontSize: 48, color: "#d1d5db" }} />
          <span>Visuals is not selected, please select the visuals from the user administration page.</span>
          <a
            href="/settings/visuals"
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "var(--app-icon-color, #a70e13)",
              textDecoration: "none",
            }}
          >
            Customize Visuals →
          </a>
        </div>
      )}

      {!Loading && reportVisuals.length > 0 && (
        <>
          {useGlobalFilters && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                alignItems: "flex-end",
                margin: "10px 0",
                padding: "10px 12px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                background: "#fafafa",
              }}
            >
              {filterControls.map((control) => (
                <label
                  key={control.key}
                  style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px", color: "#555" }}
                >
                  {control.column}
                  <select
                    value={draftSelections[control.key] ?? ALL_VALUES}
                    onChange={(event) =>
                      setDraftSelections((prev) => ({ ...prev, [control.key]: event.target.value }))
                    }
                    style={{
                      minWidth: "150px",
                      padding: "6px 8px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "13px",
                      background: "#fff",
                    }}
                  >
                    <option value={ALL_VALUES}>All</option>
                    {control.values.map((value) => (
                      <option key={String(value)} value={value}>
                        {String(value)}
                      </option>
                    ))}
                  </select>
                </label>
              ))}

              <button
                type="button"
                onClick={applyGlobalFilters}
                style={{
                  border: "none",
                  background: "var(--app-button-color, #253978)",
                  color: "#fff",
                  borderRadius: "6px",
                  padding: "7px 18px",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Apply
              </button>
            </div>
          )}

          <div className="visuals_container">
          {reportVisuals.map((ivisuals, Index) => {
            return (
              <div
                key={Index}
                style={{
                  // boxShadow: "0px 0px 4px rgba(0,0,0,0.6)",
                  height: "420px",
                  padding: "1rem",
                  borderRadius: "6px",
                  border: "2px solid #eee",
                  width: "99%",
                  paddingBottom: "5rem",
                }}
              >
                <PBIVisuals
                  report_id={ivisuals.report_id}
                  report_name={ivisuals.report_name}
                  visuals_id={ivisuals.visualName}
                  title={ivisuals.name}
                  defaultRL={defaultRL}
                  rlTypeData={rlTypeData}
                  filters = {
                    useGlobalFilters
                      ? globalFiltersByVisual.get(ivisuals.visualName) || NO_FILTERS
                      : ivisuals.filters || NO_FILTERS
                  }
                  filterOptions = {
                    useGlobalFilters
                      ? NO_FILTERS
                      : optionsByReport.get(String(ivisuals.report_id)) || NO_FILTERS
                  }
                  filter1 = {ivisuals.filter1 === "null" ? "" :ivisuals.filter1}
                  filter2 = {ivisuals.filter2 === "null" ? "" :ivisuals.filter2}
                  filter3 = {ivisuals.filter3 === "null" ? "" :ivisuals.filter3}
                  filter4 = {ivisuals.filter4 === "null" ? "" :ivisuals.filter4}
                  filter5 = {ivisuals.filter5 === "null" ? "" :ivisuals.filter5}
                  groupId={props.groupId}


                />
              </div>
            );
          })}
      
          </div>
        </>
      )}
    </>
  );
}

export default LandingKPIReport
